import type { ContenidoAyuda, ItemAyuda } from "@/ui/AyudaVista";
import { ATAJOS_VISOR, BOTONES_VISOR } from "./visorArchivo";

/**
 * Ayuda de la pestaña Correos del caso. Lo que antes iba en su «?» con
 * globito y en los tooltips de cada acción (incluido POR QUÉ salen grises).
 *
 * Mismas condiciones que `components/TabCorreos`: enviar, solo admin y soporte
 * con el caso abierto; las acciones de programados y «Regresar a bandeja»,
 * admin y soporte; «Descartar», solo admin.
 */
export function ayudaCorreosCaso({
  puedeEditar,
  cerrado,
  esAdmin,
}: {
  /** `canEdit(me)`: admin o soporte. */
  puedeEditar: boolean;
  cerrado: boolean;
  esAdmin: boolean;
}): ContenidoAyuda {
  const si = (cond: boolean, items: ItemAyuda[]) => (cond ? items : []);
  const gris = "Sale gris si todavía no configuras tu correo empresarial en Perfil.";
  return {
    manual: "proveedores",
    intro: (
      <p>
        Los correos del caso, los que llegaron y los que salieron. Desde aquí le escribes al
        cliente o al proveedor; todo sale desde tu correo empresarial.
      </p>
    ),
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          {
            label: "Fondo: Oscuro / Claro",
            text: "Cambia el fondo con que ves aquí los correos abiertos; se recuerda en este navegador. El correo real siempre va con fondo claro.",
          },
          ...si(puedeEditar && !cerrado, [
            {
              label: "+ Enviar al cliente",
              text: `Abre el editor con la plantilla de la empresa para el cliente; tu mensaje se guarda solo como borrador mientras escribes. ${gris}`,
            },
            {
              label: "+ Enviar a proveedor",
              text: `Abre el editor con la plantilla sencilla para proveedor, que lleva tu firma si la configuraste. ${gris}`,
            },
          ]),
          {
            label: "Un correo de la lista",
            text: "Tócalo para abrirlo: cuerpo completo, de quién y para quién. Tócalo otra vez para cerrarlo.",
          },
          { label: "HTML / Texto", text: "Ves el correo con su formato, o solo el texto." },
          { label: "📎 (un adjunto)", text: "Abre el adjunto en el visor, para verlo o descargarlo." },
          ...si(puedeEditar, [
            {
              label: "↗ Enviar ahora",
              text: "En un correo programado: lo manda ya, sin esperar la fecha. Sale desde el correo de quien lo programó.",
            },
            { label: "🕒 Reprogramar", text: "Cambia la fecha y hora de un correo programado." },
            {
              label: "Cancelar envío",
              text: "Cancela un correo programado: el correo se borra y ya no sale.",
            },
          ]),
          {
            label: "↩ Responder",
            text: "En un correo que llegó: le contestas al remitente en el mismo hilo, con su correo citado al final.",
          },
          {
            label: "⏰ Recordar al proveedor",
            text: "En un correo que enviamos: ¿no han contestado? Prepara un recordatorio cortés en el MISMO hilo, para el destinatario original y con el correo anterior citado. Tú lo revisas y lo mandas.",
          },
          {
            label: "↪ Reenviar",
            text: "Reenvía el correo tal cual, como en Gmail; tú pones a quién.",
          },
          {
            label: "Por qué salen grises",
            text: "Responder, Recordar al proveedor y Reenviar se apagan si no has configurado tu correo empresarial en Perfil, o si tu rol es solo de consulta.",
          },
          ...si(puedeEditar, [
            {
              label: "↺ Regresar a bandeja",
              text: "En un correo que llegó: lo quita de este caso y lo regresa a la Bandeja, para asignarlo a otro caso o descartarlo. Útil si se ligó al caso equivocado. Queda en la bitácora.",
            },
          ]),
          ...si(esAdmin, [
            {
              label: "🗑 Descartar",
              text: "Oculta el correo para siempre; queda registrado para auditoría. Solo admin.",
            },
          ]),
          ...BOTONES_VISOR,
        ],
      },
      {
        titulo: "Qué significa cada indicador",
        items: [
          {
            label: "↗ OUT",
            text: "Salió desde aquí, al cliente o a un proveedor, desde tu correo empresarial y con copia oculta a tu buzón si la activaste en Perfil. Al cliente va con el pie de la empresa; al proveedor, con tu firma si la configuraste.",
          },
          {
            label: "↘ IN",
            text: "Llegó y quedó ligado a este caso: solo (por ejemplo, porque traía el TE o un IMEI del caso en el asunto) o porque alguien lo asignó desde la Bandeja.",
          },
          {
            label: "🔔 Interno",
            text: "Aviso entre compañeros (por ejemplo, el correo de «Notificar»). No se le mandó al cliente.",
          },
          { label: "ERROR", text: "El envío falló; abajo dice por qué." },
          { label: "programado …", text: "Saldrá solo en esa fecha y hora." },
          {
            label: "ID #…",
            text: "Número interno del correo y su identificador, por si hay que rastrearlo.",
          },
        ],
      },
      { titulo: "Atajos de teclado", items: ATAJOS_VISOR },
    ],
  };
}
